import React, { useState } from 'react';
import { Download, Loader2, AlertCircle, CheckCircle, RefreshCw, Calendar } from 'lucide-react';
import { 
  Button, 
  Card, 
  CardContent, 
  CardHeader, 
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  AlertTitle,
  CircularProgress,
  Stack
} from '@mui/material';
import { styled } from '@mui/material/styles';
import HttpService from '../services/httpService';
// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  border: '2px dashed #c4b5fd',
  background: 'linear-gradient(to bottom right, #faf5ff, #ffffff)',
  boxShadow: theme.shadows[1],
}));

const InfoBox = styled(Box)(({ theme }) => ({
  backgroundColor: '#eff6ff',
  border: '1px solid #bfdbfe',
  borderRadius: '8px',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const SelectContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  flex: 1,
  minWidth: 200,
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#cbd5e1',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#94a3b8',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#8b5cf6',
    borderWidth: '2px',
  },
}));

const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

function generateMonthOptions() {
  const now = new Date();
  const options = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
    options.push({ key, label });
  }
  return options;
}

const ERPImport = ({ onDataImport }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const monthOptions = generateMonthOptions();
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0].key);

  const handleERPImport = async () => {
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    const [y, m] = selectedMonth.split('-').map(Number);
    const cmLabel = `${monthNames[m - 1]} ${y}`;
    setStatus(`Fetching leads and notes-pending data for ${cmLabel} and 2 prior months...`);

    try {
      const obj = { selected_month: selectedMonth };
      const [healthResult, notesPendingResult] = await Promise.allSettled([
        HttpService.leadHelthData(obj),
        HttpService.leadHelthNotesPending(obj),
      ]);

      const healthData =
        healthResult.status === 'fulfilled' ? healthResult.value?.data : null;
      const notesPendingData =
        notesPendingResult.status === 'fulfilled' ? notesPendingResult.value?.data : null;

      const healthOk =
        healthData?.success && Array.isArray(healthData.leads) && healthData.leads.length > 0;
      const notesOk =
        notesPendingData?.success &&
        ((Array.isArray(notesPendingData.leads) && notesPendingData.leads.length > 0) ||
          (Array.isArray(notesPendingData.summary?.byCityAndStage) &&
            notesPendingData.summary.byCityAndStage.length > 0));

      if (healthOk || notesOk) {
        const parts = [];
        if (healthOk) {
          const ecCount = healthData.experience_centers?.length || 0;
          parts.push(
            `${healthData.lead_count.toLocaleString()} leads from ${ecCount} Experience Centers`
          );
        }
        if (notesOk) {
          const notesCount =
            notesPendingData.pendingCount ?? notesPendingData.leads?.length ?? 0;
          parts.push(`${Number(notesCount).toLocaleString()} leads with notes pending`);
        }
        setSuccess(`Fetched ${parts.join(' and ')}.`);
        setStatus(null);
        setIsLoading(false);

        if (onDataImport) {
          onDataImport(
            healthOk ? healthData.leads : [],
            selectedMonth,
            notesOk ? notesPendingData : null
          );
        }
      } else {
        setError('No lead data found for the selected period.');
        setStatus(null);
        setIsLoading(false);
      }

    } catch (err) {
      setError(err.message.includes('timeout')
        ? 'Request timed out. Please try again.'
        : err.message || 'Failed to import from ERP.');
      setStatus(null);
      setIsLoading(false);
    }
  };

  return (
    <StyledCard>
      <CardHeader
        sx={{ pb: 1 }}
        avatar={
          <RefreshCw size={20} style={{ color: '#7c3aed' }} />
        }
        title={
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#581c87' }}>
            Import from ERP
          </Typography>
        }
      />
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Info Box */}
        <InfoBox>
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e40af', mb: 0.5 }}>
            Select a month and fetch lead data
          </Typography>
          <Typography variant="caption" sx={{ color: '#2563eb' }}>
            Data will be shown for the selected month (CM) and two prior months (CM-1, CM-2)
          </Typography>
        </InfoBox>

        {/* Select and Button */}
        <SelectContainer>
          <StyledFormControl size="medium" disabled={isLoading}>
            <InputLabel 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                '&.Mui-focused': {
                  color: '#8b5cf6',
                },
              }}
            >
              <Calendar size={16} style={{ marginRight: '4px' }} />
              Select Month
            </InputLabel>
            <StyledSelect
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              label="Select Month"
              data-testid="erp-month-select"
              disabled={isLoading}
              sx={{
                '& .MuiSelect-select': {
                  py: 1.5,
                },
              }}
            >
              {monthOptions.map(opt => (
                <MenuItem key={opt.key} value={opt.key}>
                  {opt.label}
                </MenuItem>
              ))}
            </StyledSelect>
          </StyledFormControl>

          <Button
            variant="contained"
            onClick={handleERPImport}
            disabled={isLoading}
            data-testid="erp-import-btn"
            sx={{
              backgroundColor: '#7c3aed',
              '&:hover': {
                backgroundColor: '#6d28d9',
              },
              whiteSpace: 'nowrap',
              minWidth: '120px',
            }}
            startIcon={isLoading ? null : <Download size={18} />}
          >
            {isLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Fetching...
              </Box>
            ) : (
              'Fetch Leads'
            )}
          </Button>
        </SelectContainer>

        {/* Status Indicator */}
        {status && (
          <Alert 
            severity="info" 
            icon={<Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />}
            sx={{ 
              '& .MuiAlert-icon': {
                alignItems: 'center',
              },
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          >
            {status}
          </Alert>
        )}

        {/* Error Alert */}
        {error && (
          <Alert 
            severity="error"
            icon={<AlertCircle size={18} />}
            sx={{
              '& .MuiAlert-message': {
                width: '100%',
              },
            }}
          >
            <Typography variant="body2">{error}</Typography>
          </Alert>
        )}

        {/* Success Alert */}
        {success && (
          <Alert 
            severity="success"
            icon={<CheckCircle size={18} />}
          >
            <Typography variant="body2">{success}</Typography>
          </Alert>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default ERPImport;